import styles from "../UserAdminModal.module.css";
import PasswordRequirementsChecklist, { isPasswordStrong } from "../PasswordRequirementsChecklist";
import ProgressReportSection from "../ProgressReportSection";
import { useUserAdminContext } from "../../context/UserAdminContext";
import { canProfessorManageStudent, ROLE_LABELS } from "./userAdminHelpers";
import BrandingSection from "./BrandingSection";
import OrganizationAdminSection from "./OrganizationAdminSection";

// Extraído de UserAdminModal.jsx (issue 012, docs/07-issues.md) — aba "Form"
// (criar/editar usuário + redefinir senha de outro usuário). Comportamento
// idêntico, só mudou de arquivo; estado continua todo em UserAdminModal.jsx,
// consumido via useUserAdminContext(). O guard externo (tab === "form") fica
// no componente-pai — aqui dentro só os dois sub-guards que já existiam
// (formulário principal vs. seção de redefinir senha) continuam iguais.
export default function UserFormTab() {
  const {
    currentUser,
    editingUser,
    form,
    setForm,
    handleChange,
    handleSubmit,
    allowedCreateRoles,
    professors,
    saving,
    resetUserForm,
    adminPwForm,
    setAdminPwForm,
    handleAdminPasswordSubmit,
  } = useUserAdminContext();

  return (
    <>
      {currentUser.role !== "aluno" && currentUser.role !== "google" && (
        <form className={styles.uamForm} onSubmit={handleSubmit}>
          <div className={styles.uamField}>
            <label>Nome</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Nome completo"
              required
            />
          </div>

          <div className={styles.uamField}>
            <label>Email</label>
            <input
              type="email"
              name="email"
              autoComplete="off"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              placeholder="email@exemplo.com"
              required
            />
          </div>

          {!editingUser && (
            <div className={styles.uamField}>
              <label>Senha</label>
              <input
                type="password"
                name="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => handleChange("password", e.target.value)}
                placeholder="••••••••"
                required
              />
              {form.password && <PasswordRequirementsChecklist password={form.password} />}
            </div>
          )}


          <div className={styles.uamGrid}>
            <div className={styles.uamField}>
              <label>Tipo</label>
              <select
                value={form.role}
                onChange={(e) => handleChange("role", e.target.value)}
                disabled={currentUser.role === "professor"}
              >
                {allowedCreateRoles.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>

            {currentUser.role === "admin" && !editingUser && form.role === "professor" && (
              <div className={styles.uamField}>
                <label htmlFor="uam-org-name">Academia (opcional)</label>
                <input
                  id="uam-org-name"
                  type="text"
                  value={form.organization_name || ""}
                  onChange={(e) => handleChange("organization_name", e.target.value)}
                  placeholder="Ex.: Academia Movimento"
                  maxLength={120}
                  autoComplete="off"
                />
                <small className={styles.uamHelper}>
                  Preencha para criar a academia com este professor como dono. Todos os professores e alunos da academia usam a marca dele.
                </small>
              </div>
            )}

            {currentUser.role === "admin" && form.role === "aluno" && (
              <div className={styles.uamField}>
                <label>Professor responsável</label>
                <select
                  value={form.professor_id}
                  onChange={(e) => handleChange("professor_id", e.target.value)}
                >
                  <option value="">Sem professor</option>
                  {professors.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {currentUser.role === "professor" && (
            <div className={styles.uamNote}>
              O aluno criado por professor será vinculado automaticamente ao professor logado.
            </div>
          )}

          {(currentUser.role === "admin" || currentUser.role === "professor") && (
            <div className={styles.uamField}>
              <label>Meta semanal (treinos/sem)</label>
              <input
                type="number"
                min={0}
                max={14}
                value={form.weekly_goal}
                onChange={(e) => handleChange("weekly_goal", e.target.value)}
              />
              <small className={styles.uamHelper}>Usado para calcular a taxa "Meta batida (%)". Padrão: 3.</small>
            </div>
          )}

          {(currentUser.role === "admin" || currentUser.role === "professor") && editingUser && (
            <div className={styles.uamField}>
              <label>Vencimento plano</label>
              <input
                type="date"
                value={form.plan_expires_at || ""}
                onChange={(e) => handleChange("plan_expires_at", e.target.value)}
              />
              <small className={styles.uamHelper}>
                {form.plan_expires_at
                  ? `Ao atingir esta data o login do aluno será bloqueado automaticamente.`
                  : "Sem vencimento definido. Deixe em branco para acesso ilimitado."}
              </small>
            </div>
          )}

          {editingUser && currentUser.role === "admin" && (
            <div className={styles.uamField}>
              <label className={styles.uamToggleLabel}>
                <span>Conta ativa</span>
                <button
                  type="button"
                  className={`${styles.uamToggle} ${form.is_active ? styles.toggleOn : styles.toggleOff}`}
                  onClick={() => handleChange("is_active", !form.is_active)}
                  aria-pressed={form.is_active}
                >
                  <span className={styles.uamToggleThumb} />
                </button>
              </label>
              <small className={styles.uamHelper}>
                {form.is_active ? "Usuário pode fazer login normalmente." : "Login bloqueado para este usuário."}
              </small>
            </div>
          )}

          {editingUser && currentUser.role === "admin" && ( form.role === "aluno" || form.role === "professor" ) && (
            <div className={styles.uamField}>
              <label className={styles.uamToggleLabel}>
                <span>Self-service</span>
                <button
                  type="button"
                  className={`${styles.uamToggle} ${form.is_self_service ? styles.toggleOn : styles.toggleOff}`}
                  onClick={() => handleChange("is_self_service", !form.is_self_service)}
                  aria-pressed={form.is_self_service}
                >
                  <span className={styles.uamToggleThumb} />
                </button>
              </label>
              <small className={styles.uamHelper}>
                {form.role === "professor"
                  ? form.is_self_service
                    ? "Exige assinatura ou pagamento avulso."
                    : "Não exige assinatura ou pagamento avulso."
                  : form.role === "aluno"
                    ? form.is_self_service
                      ? 'Gerou o próprio treino com IA sem professor - fica fora da lista de "alunos sem professor" dos professores.'
                      : "Tratado normalmente (não é self-service)."
                    : ""}
              </small>
            </div>
          )}

          {editingUser && currentUser.role === "admin" && form.role === "professor" && !form.is_self_service && (
            <div className={styles.uamField}>
              <label className={styles.uamToggleLabel}>
                <span>Gestão de pagamento dos alunos</span>
                <button
                  type="button"
                  className={`${styles.uamToggle} ${form.student_billing_override ? styles.toggleOn : styles.toggleOff}`}
                  onClick={() => handleChange("student_billing_override", !form.student_billing_override)}
                  aria-pressed={form.student_billing_override}
                >
                  <span className={styles.uamToggleThumb} />
                </button>
              </label>
              <small className={styles.uamHelper}>
                {form.student_billing_override
                  ? "Não paga a plataforma, mas pode cobrar mensalidade dos próprios alunos."
                  : "Sem acesso a nenhuma tela de pagamento (nem da plataforma, nem dos alunos)."}
              </small>
            </div>
          )}

          {/* ── Relatório de progresso - apenas admin editando ALUNO ──
              Fica no perfil do aluno de propósito: os números são dele,
              e é aqui que o admin já está olhando quando decide enviar.
              A primeira versão da feature era um botão solto e foi
              revertida por isso (ver docs/07-issues.md). */}
          {editingUser && currentUser.role === "admin" && form.role === "aluno" && (
            <ProgressReportSection
              studentId={editingUser.id}
              studentName={editingUser.name}
            />
          )}

          {/* ── Academia - admin editando PROFESSOR: converter em academia
              (a academia que hoje usa o perfil de professor) ou ver de qual
              academia ele faz parte. key reinicia o estado ao trocar de usuário. */}
          {editingUser && currentUser.role === "admin" && form.role === "professor" && (
            <OrganizationAdminSection key={editingUser.id} professor={editingUser} />
          )}

          {editingUser && currentUser.role === "admin" && form.role === "professor"
            && editingUser.organization_id && editingUser.org_role && editingUser.org_role !== "owner" && (
            <div className={styles.uamNote}>
              Este professor usa a marca da academia {editingUser.organization_name}, definida pelo dono. Para alterar, edite o perfil do dono.
            </div>
          )}

          {editingUser && currentUser.role === "admin" && form.role === "professor"
            && !(editingUser.organization_id && editingUser.org_role && editingUser.org_role !== "owner") && (
            <BrandingSection
              mode="admin"
              owner={editingUser}
              brandColor={form.brand_color}
              onBrandColorChange={(hex) => setForm((f) => ({ ...f, brand_color: hex }))}
              bgColorDark={form.brand_bg_color_dark}
              onBgColorDarkChange={(hex) => setForm((f) => ({ ...f, brand_bg_color_dark: hex }))}
              bgColorLight={form.brand_bg_color_light}
              onBgColorLightChange={(hex) => setForm((f) => ({ ...f, brand_bg_color_light: hex }))}
            />
          )}

          <div className={styles.uamFooter}>
            <button
              type="button"
              className={`${styles.uamBtn} ${styles.ghost}`}
              onClick={resetUserForm}
              disabled={saving}
            >
              Limpar
            </button>

            <button
              type="submit"
              className={`${styles.uamBtn} ${styles.primary}`}
              disabled={
                saving ||
                (!editingUser && !isPasswordStrong(form.password))
              }
            >
              {editingUser
                ? "Salvar alterações"
                : currentUser.role === "professor"
                ? "Criar aluno"
                : "Criar usuário"}
            </button>
          </div>
        </form>
      )}

      {/* Seção dedicada de senha - admin editando outro usuário */}
      {editingUser && (
        currentUser.role === "admin" ||
        // Professor redefine a senha de aluno que pode gerenciar: o dele ou,
        // numa academia, o de qualquer colega (rodízio). Nunca a própria. O
        // backend reforça a mesma regra via can_manage_target.
        (currentUser.role === "professor" &&
          canProfessorManageStudent(currentUser, editingUser) &&
          editingUser.id !== currentUser.id)
      ) && (
        <form
          className={styles.uamPasswordForm}
          onSubmit={handleAdminPasswordSubmit}
          autoComplete="off"
        >
          <h4 className={styles.uamSectionTitle}>
            {(editingUser.auth_provider === "google" || editingUser.auth_provider === "apple") ? "Criar senha" : "Redefinir senha"}
          </h4>

          {(editingUser.auth_provider === "google" || editingUser.auth_provider === "apple") && (
            <div className={styles.uamNote}>
              Este usuário entrou com {editingUser.auth_provider === "apple" ? "Apple" : "Google"} e ainda não tem senha local. Ao criar uma senha, ele poderá acessar também com email e senha.
            </div>
          )}

          <div className={styles.uamField}>
            <label>Nova senha</label>
            <input
              type="password"
              name="admin_new_password"
              autoComplete="new-password"
              value={adminPwForm.new_password}
              onChange={(e) => setAdminPwForm((p) => ({ ...p, new_password: e.target.value }))}
              placeholder="Digite a nova senha"
            />
          </div>

          <div className={styles.uamField}>
            <label>Confirmar nova senha</label>
            <input
              type="password"
              name="admin_confirm_password"
              autoComplete="new-password"
              value={adminPwForm.confirm_password}
              onChange={(e) => setAdminPwForm((p) => ({ ...p, confirm_password: e.target.value }))}
              placeholder="Repita a nova senha"
            />
          </div>

          <div className={styles.uamFooter}>
            <button
              type="submit"
              className={`${styles.uamBtn} ${styles.primary}`}
              disabled={saving}
            >
              {(editingUser.auth_provider === "google" || editingUser.auth_provider === "apple") ? "Criar senha" : "Redefinir senha"}
            </button>
          </div>
        </form>
      )}
    </>
  );
}
